import React from "react";
import { Box, CircularProgress, Typography, useTheme } from "@mui/material";
import { useTranslations } from "next-intl";

interface MiningTableProps {
  miningData: any[];
  isLoading: boolean;
}

// 统一列配置，保证宽度和对齐一致
const columns = [
  {
    key: "rank",
    labelKey: "rank",
    flex: "0 0 65px",
    minWidth: 65,
    maxWidth: 65,
    textAlign: "center",
  },
  {
    key: "coin",
    labelKey: "coin",
    flex: "0 0 140px",
    minWidth: 140,
    maxWidth: 140,
    textAlign: "left",
  },
  {
    key: "dailyRevenue",
    labelKey: "dailyRevenue",
    flex: "0 0 160px",
    minWidth: 160,
    maxWidth: 160,
    textAlign: "right",
  },
  {
    key: "marketCap",
    labelKey: "marketCap",
    flex: "0 0 120px",
    minWidth: 120,
    maxWidth: 120,
    textAlign: "right",
  },
  {
    key: "price",
    labelKey: "price",
    flex: "0 0 120px",
    minWidth: 120,
    maxWidth: 120,
    textAlign: "right",
  },
  {
    key: "volume",
    labelKey: "volume",
    flex: "0 0 120px",
    minWidth: 120,
    maxWidth: 120,
    textAlign: "right",
  },
  {
    key: "powProduced",
    labelKey: "powProduced",
    flex: "0 0 120px",
    minWidth: 120,
    maxWidth: 160,
    textAlign: "right",
  },
  {
    key: "hashrate",
    labelKey: "hashrate",
    flex: "0 0 120px",
    minWidth: 120,
    maxWidth: 120,
    textAlign: "right",
  },
  {
    key: "algorithm",
    labelKey: "algorithm",
    flex: "0 0 140px",
    minWidth: 140,
    maxWidth: 140,
    textAlign: "right",
  },
];

const totalMinWidth = columns.reduce((sum, col) => sum + (col.minWidth || 0), 0);

export default function MiningTable({ miningData, isLoading }: MiningTableProps) {
  const t = useTranslations();
  const theme = useTheme();
  // 拖拽横向滚动
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const isDragging = React.useRef(false);
  const lastX = React.useRef(0);

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    isDragging.current = true;
    lastX.current = e.clientX;
    document.body.style.cursor = "grabbing";
  };
  const handleMouseMove = (e: MouseEvent) => {
    if (isDragging.current && scrollRef.current) {
      const dx = lastX.current - e.clientX;
      scrollRef.current.scrollLeft += dx;
      lastX.current = e.clientX;
    }
  };
  const handleMouseUp = () => {
    isDragging.current = false;
    document.body.style.cursor = "auto";
  };
  React.useEffect(() => {
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  // 通用 cell 样式生成器
  const getCellSx = (col: any, colIdx: number, rowIdx: number, isHeader = false) => ({
    minWidth: col.minWidth,
    maxWidth: col.maxWidth,
    flex: {
      xs: `0 0 ${col.minWidth}px`,
      // md: col.flex,
      md: 1, // 大屏下所有列等分填满
    },
    textAlign: col.textAlign,
    // whiteSpace: "nowrap",
    px: { xs: 0.5, sm: 1 },
    py: 2,
    color: isHeader ? "text.secondary" : "text.primary",
    fontWeight: isHeader ? 400 : col.key === "rank" ? 600 : 400,
    fontSize: isHeader ? "var(--font-size-s)" : "var(--font-size-m)",
    backgroundColor: isHeader ? "background.paper" : "background.default",
    borderBottom: isHeader ? "none" : "none",
    ...(colIdx === 0 && { pl: "1.5rem !important" }),
    ...(colIdx === columns.length - 1 && { pr: "1.5rem !important" }),
    ...(rowIdx === 0 && colIdx === 0 && { borderTopLeftRadius: "1.5rem" }),
    ...(rowIdx === 0 && colIdx === columns.length - 1 && { borderTopRightRadius: "1.5rem" }),
    display: "flex",
    justifyContent: col.textAlign === "right" ? "flex-end" : col.textAlign === "center" ? "center" : "flex-start",
    alignItems: "center",
    boxSizing: "border-box",
  });

  // 渲染单元格内容
  const renderCellContent = (col: any, item: any) => {
    if (col.key === "rank") {
      return (
        <Typography variant="body2" color="text.primary" fontWeight={600}>
          {item.rank}
        </Typography>
      );
    }
    if (col.key === "coin") {
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box sx={{ width: "1.75rem" }}>
            <img
              src={`/images/icon/mining/${item.coin.toLowerCase()}.png`}
              alt={item.coin}
              style={{ width: "1.75rem", height: "1.75rem", objectFit: "contain", borderRadius: "50%" }}
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          </Box>
          <Typography variant="body1" color="text.primary">
            {item.coin.toUpperCase()}
          </Typography>
        </Box>
      );
    }
    return (
      <Typography variant="body1" color="text.primary">
        {item[col.key]}
      </Typography>
    );
  };

  return (
    <Box
      ref={scrollRef}
      sx={{
        width: "fit-content",
        boxSizing: "border-box",
        overflowX: "auto",
        px: { xs: "1rem", lg: "0" },
        userSelect: "none",
        scrollbarWidth: "none",
        "&::-webkit-scrollbar": { display: "none" },
        cursor: isDragging.current ? "grabbing" : "grab",
      }}
      onMouseDown={handleMouseDown}>
      {/* Header Row */}
      <Box
        sx={{
          minWidth: totalMinWidth,
          width: { xs: "auto", lg: "100%" },
          boxSizing: "border-box",
          pb: "1.5rem",
          display: "flex",
          backgroundColor: "background.paper",
          border: "1px solid",
          borderColor: "background.paper",
          borderRadius: "1.5rem 1.5rem 0 0",
          overflow: "hidden",
        }}>
        {columns.map((col, colIdx) => (
          <Box key={col.key} sx={getCellSx(col, colIdx, 0, true)}>
            {t(`miningHub.table.columns.${col.labelKey}` as any)}
          </Box>
        ))}
      </Box>
      {/* Body Rows */}
      <Box
        sx={{
          minWidth: totalMinWidth,
          boxSizing: "border-box",
          mt: "-1.5rem",
          // backgroundColor: "background.paper",
          borderRadius: "1.5rem",
          border: "1px solid",
          borderColor: theme.palette.mode === "dark" ? "background.paper" : "#D9DCDE",
          maxHeight: `calc(60px * 20 + 8px)`, // 20行高度
          overflowY: "auto",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}>
        {isLoading ? (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 8 }}>
            <CircularProgress color="primary" sx={{ mb: 2 }} />
          </Box>
        ) : (
          <Box sx={{ py: 1, backgroundColor: "background.default", borderRadius: "1.5rem" }}>
            {miningData.map((item: any, rowIdx: number) => (
              <Box key={`${item.coin}-${rowIdx}`} sx={{ display: "flex" }}>
                {columns.map((col, colIdx) => (
                  <Box key={col.key} sx={getCellSx(col, colIdx, rowIdx, false)}>
                    {renderCellContent(col, item)}
                  </Box>
                ))}
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}
